import type { FAQ } from './types';

/**
 * Platform-admin only FAQs. Loaded ONLY when tenant_type='platform'.
 * Covers operations that are restricted on the backend by
 * RequirePlatform middleware (carrier provider config + creds, audit
 * log scope, cross-tenant memberships, etc).
 */
export const PLATFORM_FAQS_ES: FAQ[] = [
  {
    id: 'platform-add-carrier',
    category: 'platform',
    question: '¿Cómo agrego un nuevo carrier (provider)?',
    tags: ['carrier', 'provider', 'twilio', 'telnyx'],
    cta: { label: 'Ir a Providers', to: '/admin/providers' },
    answer: (
      <p>
        Admin → <strong>Providers</strong> → "Crear". El nombre debe matchear
        un adapter en el código (<code className="mono">twilio</code>,
        <code className="mono"> bandwidth</code>, etc). Los providers sin
        adapter se persisten pero el dispatcher los salta hasta que se
        sume su implementación.
      </p>
    ),
  },
  {
    id: 'platform-carrier-creds',
    category: 'platform',
    question: '¿Dónde se guardan las credenciales del carrier?',
    tags: ['secrets', 'credentials', 'encrypted'],
    answer: (
      <p>
        En la columna <code className="mono">providers.secret_config_encrypted</code>
        cifrada con AES-256-GCM usando la <code className="mono">SECRETS_ENCRYPTION_KEY</code>
        del server. El servidor las descifra al cargar el adapter; nunca
        quedan en plaintext en la DB. Si rotás la key, los providers se
        rompen hasta re-encriptarlos.
      </p>
    ),
  },
  {
    id: 'platform-carrier-health',
    category: 'platform',
    question: '¿Cómo funciona el health monitoring de carriers?',
    tags: ['health', 'failover', 'monitor'],
    answer: (
      <p>
        El health-monitor worker corre cada minuto, manda un ping al carrier
        y actualiza <code className="mono">providers.health_status</code>{' '}
        (healthy / degraded / down). El dispatcher excluye carriers en
        "down" del cascade automáticamente — failover transparente para el
        envío. Auto-disable si un carrier falla N veces seguidas.
      </p>
    ),
  },
  {
    id: 'platform-twilio-setup',
    category: 'platform',
    question: '¿Qué env vars necesita Twilio?',
    tags: ['twilio', 'env', 'config'],
    answer: (
      <ul className="list-disc ml-5 space-y-1">
        <li><code className="mono">TWILIO_ACCOUNT_SID</code> — empieza con AC...</li>
        <li><code className="mono">TWILIO_AUTH_TOKEN</code> — el secret de tu account</li>
        <li><code className="mono">TWILIO_FROM_NUMBER</code> — número E.164 del que mandás</li>
        <li><code className="mono">TWILIO_BASE_URL</code> — opcional, default api.twilio.com</li>
        <li><code className="mono">TWILIO_STATUS_CALLBACK</code> — URL pública del dlr-receiver</li>
      </ul>
    ),
  },
  {
    id: 'platform-dlr-receiver',
    category: 'platform',
    question: '¿Qué hace el dlr-receiver?',
    tags: ['dlr', 'webhooks', 'callback'],
    answer: (
      <p>
        Es un binario aparte que escucha en <code className="mono">/webhooks/{`{provider}`}/dlr</code>
        y recibe los status callbacks de los carriers (delivery confirmation,
        failure, etc). Sin él corriendo, los mensajes quedan en
        <code className="mono"> sent</code> para siempre — el carrier nos avisa
        del <code className="mono">delivered</code> pero no llega a la DB.
      </p>
    ),
  },
  {
    id: 'platform-memberships',
    category: 'platform',
    question: '¿Cómo le doy a un user acceso a múltiples workspaces?',
    tags: ['memberships', 'multi-tenant'],
    answer: (
      <p>
        En el detail de un customer hay una card "Acceso a workspaces"
        donde podés asignar memberships cross-tenant. El user usa el
        switcher del topbar para saltar entre tenants. La JWT se reissua
        con el tenant_id correcto al switchear (POST /auth/switch-tenant).
      </p>
    ),
  },
  {
    id: 'platform-audit-cross-tenant',
    category: 'platform',
    question: '¿El audit log de un reseller puede ver acciones de otro?',
    tags: ['audit', 'isolation', 'tenant'],
    answer: (
      <p>
        No. El audit_log se filtra por tenant_id del actor — cada reseller
        ve solo sus propias mutations. La platform admin sí ve todo (es
        super-tenant). Si dos resellers querían ver el mismo log, sería un
        leak.
      </p>
    ),
  },
  {
    id: 'platform-secrets-rotate',
    category: 'platform',
    question: '¿Cómo roto las credenciales (Twilio, JWT, DB)?',
    tags: ['rotate', 'secrets', 'security'],
    answer: (
      <p>
        En el server, editá <code className="mono">/root/EzSender/infra/ovh-setup/config.env</code>{' '}
        con el nuevo valor, después corré <code className="mono">.\deploy.ps1</code>{' '}
        que re-renderiza el env runtime + restartea los servicios. JWT_SECRET
        rota → todos los users tienen que volver a logear (los tokens viejos
        se invalidan). DB password → también actualizar el ALTER USER en
        Postgres.
      </p>
    ),
  },
];
